import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {catchError, delay, finalize} from 'rxjs/operators';
import {UIService} from '../ui.service';
import {Location} from '@angular/common';
import {EnvironmentService} from 'bos-commons';
import {HtmlPage} from '../../admin/admin-footer/html-page';
import {SessionCounter} from '../../admin/logged-users/session-counter';
import {HtmlAnchor} from '../../admin/admin-links/html-anchor';
import {SessionConfiguration} from "../../admin/admin-session-config/session-configuration";
import {LevenshteinConfiguration} from "../../admin/admin-levenshtein-config/levenshtein-configuration";
import {GitVersionInfo} from "./git-version-info";

@Injectable({
  providedIn: 'root'
})
export class RestService {

  private static LOGOUT_URLS = "/logged/urls/logout";

  public appName: string;

  constructor(private http: HttpClient,
              private location: Location,
              public env: EnvironmentService,
              private UI: UIService) {
  }

  public getVersion() {
    return this.rest(this.http.get(this.getUrl(this.env.environment.backendBaseUrl + this.env.environment.versionURL)), false);
  }

  public getFrameworksVersions(): Observable<GitVersionInfo[]> {
    return this.rest(this.http.get<GitVersionInfo[]>(this.getUrl(this.env.environment.backendBaseUrl + this.env.environment.frameworkVersionsURL)), false);
  }


  public getLogout(): String {
    return this.env.environment.backendBaseUrl + "/logout";
  }

  public messages(key: string) {
    const url = this.location.prepareExternalUrl(`/assets/messages_${key}.properties`);
    return this.rest(this.http.get(url, {responseType: 'text'}));
  }

  public authenticate(username: string, password: string, rememberMe: boolean, domain: string): Observable<any> {
    const url = this.env.environment.backendBaseUrl + '/login';
    let headers = new HttpHeaders().append("Content-Type", "application/x-www-form-urlencoded")
    return this.http.post(url, `username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&rememberMe=${encodeURIComponent(rememberMe)}&domain=${encodeURIComponent(domain)}`, {headers: headers, observe: 'response', responseType: 'text', withCredentials: true});
  }

  public code(code: string): Observable<any> {
    const url = this.env.environment.backendBaseUrl + '/code';
    return this.http.post(url, {code: code},{withCredentials: true, observe: 'response', responseType: 'text'});
  }

  public getLogoutUrls(): Observable<{ logoutUrl: string, useGet: boolean} []> {
    const url = this.env.environment.backendBaseUrl + RestService.LOGOUT_URLS;
    return this.http.get<{ logoutUrl: string, useGet: boolean} []>(url, {withCredentials: true});
  }

  public remind(email: string, username: string, domain: string): Observable<any> {
    const url = this.env.environment.backendBaseUrl + '/remind';
    return this.http.post(url, {email: email, userName: username, domain: domain});
  }

  public reset(token: string, username: string, password: string, domain: string): Observable<any> {
    const url = this.env.environment.backendBaseUrl + '/reset';
    return this.http.post(url, {token: token, userName: username, password: password, domain: domain});
  }

  public updateExpiredPassword(oldPass: string, newPass: string, confirmPass: string): Observable<any> {
    const url = this.env.environment.backendBaseUrl + '/logged/password/reset';
    return this.http.post(url, {oldPassword: oldPass, newPassword: newPass, confirmPassword: confirmPass}, {withCredentials: true});
  }

  public getHtmlPage(id: any): Observable<HtmlPage> {
    const url = this.env.environment.backendBaseUrl + this.env.environment.onePageUrl + "/" + id;

    return this.http.get<HtmlPage>(url, {withCredentials: true});
  }

  savePage(htmlPage: HtmlPage, type: string): Observable<any> {
    const url = this.env.environment.backendBaseUrl + this.env.environment.savePageUrl + `/${type}` ;

    return this.http.post(url, htmlPage, {withCredentials: true});
  }

  deletePage(id: number): Observable<any> {
    const url = this.env.environment.backendBaseUrl + this.env.environment.savePageUrl + `/${id}` ;
    return this.http.delete(url, {withCredentials: true});
  }

  public getAllAnchors(): Observable<HtmlAnchor[]> {
    const url = this.env.environment.backendBaseUrl + this.env.environment.allAnchorsUrl;
    return this.http.get<HtmlAnchor[]>(url, {withCredentials: true})
  }

  public getHtmlAnchor(id: any): Observable<HtmlAnchor> {
    const url = this.env.environment.backendBaseUrl + this.env.environment.oneAnchorUrl + "/" + id;

    return this.http.get<HtmlAnchor>(url, {withCredentials: true});
  }

  saveAnchor(htmlAnchor: HtmlAnchor): Observable<any> {
    const url = this.env.environment.backendBaseUrl + this.env.environment.allAdminAnchorsUrl;

    return this.http.post(url, htmlAnchor, {withCredentials: true});
  }

  deleteAnchor(id: number): Observable<any> {
    const url = this.env.environment.backendBaseUrl + this.env.environment.allAdminAnchorsUrl + `/${id}` ;
    return this.http.delete(url, {withCredentials: true});
  }

  getSessionCounter(): Observable<SessionCounter> {
    const url = this.env.environment.backendBaseUrl + this.env.environment.sessionCounterUrl;

    return this.http.get<SessionCounter>(url, {withCredentials: true});
  }

  isUserLogged(): Observable<boolean> {
    const url = this.env.environment.backendBaseUrl + this.env.environment.checkLogged;

    return this.http.get<boolean>(url, {withCredentials: true});
  }

  isAdminLogged(): Observable<boolean> {
    const url = this.env.environment.backendBaseUrl + this.env.environment.checkAdmin;

    return this.http.get<boolean>(url, {withCredentials: true});
  }

  getSessionConfig(): Observable<SessionConfiguration> {
    const url = this.env.environment.backendBaseUrl + this.env.environment.sessionConfigUrl;
    return this.http.get<SessionConfiguration>(url, {withCredentials: true});
  }

  updateSessionConfig(config: SessionConfiguration): Observable<any> {
    const url = this.env.environment.backendBaseUrl + this.env.environment.sessionConfigUrl;

    return this.http.post(url, config, {withCredentials: true});
  }

  getLevenshteinConfig(): Observable<LevenshteinConfiguration> {
    const url = this.env.environment.backendBaseUrl + this.env.environment.levenshteinConfigUrl;
    return this.http.get<LevenshteinConfiguration>(url, {withCredentials: true});
  }

  getLevenshteinConfigUnsecured(): Observable<LevenshteinConfiguration> {
    const url = this.env.environment.backendBaseUrl + this.env.environment.levenshteinConfigUnsecuredUrl;
    return this.http.get<LevenshteinConfiguration>(url, {withCredentials: true});
  }

  updateLevenshteinConfig(config: LevenshteinConfiguration): Observable<any> {
    const url = this.env.environment.backendBaseUrl + this.env.environment.levenshteinConfigUrl;

    return this.http.post(url, config, {withCredentials: true});
  }

  getDefaultRedirectUrl(): Observable<string> {
    const url = this.env.environment.backendBaseUrl + this.env.environment.redirectUrl;
    return this.http.get(url, {withCredentials: true, responseType: 'text'});
  }

  public getOauth2Properties(): Observable<{ url: string, name: string,style: string, customName?: string, customIcon?: string }[]> {
    const url = this.env.environment.backendBaseUrl + this.env.environment.oauth2LoginProperties;
    return this.http.get<{ url: string, name: string,style: string, customName?: string, customIcon?: string }[]>(url, {withCredentials: true});
  }

  public getContent(url: string): Promise<any> {
     return this.http.get(url, {observe: 'response', responseType: 'text'}).toPromise()
         .then(response => {
           if(response.status === 204) {
             return Promise.reject("No content!")
           } else if(response.ok && response.body !== null) {
             return Promise.resolve(response.body);
           }
           return Promise.reject("Wrong response!");
         }, error => {
           Promise.reject("Error during getting footer")
         });

  }

  public getDomainNames(): Observable<string[]>{
    return this.http.get<string[]>(this.env.environment.backendBaseUrl + this.env.environment.domainNamesUrl);
  }

  private rest<T>(obs: Observable<T>, catchErrors = true): Observable<T> {
    if (this.UI) {
      setTimeout(() => this.UI.loadingSubject.next(true));
    }
    return obs.pipe(
      delay(0), catchErrors ? catchError(err => {
        this.UI.alertService.error('rest.service.error', ['getConfig', err.message]);
        return of<T>();
      }) : err => err,
      finalize(() => this.UI.loadingSubject.next(false))
    );
  }


  private getUrl(url: string) {
    return url.replace('{appName}', this.appName);
  }

  public makePost<T>(url: string, body?: any): Observable<T> {
    return this.http.post<T>(url, body, {withCredentials: true});
  }

  public makeGet<T>(url: string, body?: any): Observable<T> {
    return this.http.get<T>(url, {withCredentials: true});
  }

  public handleError(error: any) {
    console.warn("there is an error! Check logs!", error);
    try {
      if (typeof error.error === 'string') {
        this.UI.alertService.error(JSON.parse(error.error)['message']);
      } else {
        this.UI.alertService.error(error.error['message']);
      }
    } catch (e) {
      this.UI.alertService.error('SEC_INTERNAL_ERROR')
    }
  }
}
